import type { Category } from '../types'
import { appendAuditLog } from './auditLog'
import { useFirestore } from './firebase'
import * as firestoreCategories from './firestore/categories'
import { sortCategoriesByIndex } from './menuSortOrder'
import {
  pickNextTableAreaColor,
  UNASSIGNED_TABLE_AREA_COLOR,
} from './tableAreaStorage'

const STORAGE_KEY = 'wortelogy-categories'

export const UNCATEGORIZED_CATEGORY_ID = '__uncategorized__'
export const UNCATEGORIZED_NAME = 'Uncategorized'

const uncategorizedCategory: Category = {
  id: UNCATEGORIZED_CATEGORY_ID,
  name: UNCATEGORIZED_NAME,
  color: UNASSIGNED_TABLE_AREA_COLOR,
  sortIndex: 0,
}

function defaultCategoryColor(category: Category, indexAmongCustom: number): string {
  if (category.id === UNCATEGORIZED_CATEGORY_ID) {
    return category.color?.trim() || UNASSIGNED_TABLE_AREA_COLOR
  }
  return category.color?.trim() || pickNextTableAreaColor(indexAmongCustom)
}

function withDefaultCategoryColors(list: Category[]): Category[] {
  const custom = list.filter((c) => c.id !== UNCATEGORIZED_CATEGORY_ID)
  return list.map((c) => {
    const idx = custom.findIndex((x) => x.id === c.id)
    const indexAmongCustom = idx >= 0 ? idx : custom.length
    return { ...c, color: defaultCategoryColor(c, indexAmongCustom) }
  })
}

function readCategoriesLocalUnsorted(): Category[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    let list: Category[] = raw ? (JSON.parse(raw) as Category[]) : []
    const hasUncategorized = list.some((c) => c.id === UNCATEGORIZED_CATEGORY_ID)
    if (!hasUncategorized) {
      list = [uncategorizedCategory, ...list]
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
    }
    return list
  } catch {
    return [uncategorizedCategory]
  }
}

function getCategoriesLocal(): Category[] {
  return withDefaultCategoryColors(sortCategoriesByIndex(readCategoriesLocalUnsorted()))
}

function saveCategoriesLocal(categories: Category[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(categories))
}

function generateCategoryId(): string {
  return `C-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function getCategories(): Category[] {
  if (useFirestore()) {
    // Only use place-scoped Firestore cache; never fall back to global localStorage
    // to avoid leaking another account/place's categories.
    return firestoreCategories.getCategories()
  }
  return getCategoriesLocal()
}

export function saveCategories(categories: Category[]): void {
  if (useFirestore()) {
    firestoreCategories.saveCategories(categories)
    return
  }
  const withIdx = categories.map((c, i) => ({ ...c, sortIndex: i }))
  saveCategoriesLocal(withIdx)
}

export function addCategory(category: Omit<Category, 'id'>): Category {
  const full = useFirestore()
    ? firestoreCategories.addCategory(category)
    : (() => {
        const id = generateCategoryId()
        const list = readCategoriesLocalUnsorted()
        const maxIdx = list.reduce((m, c) => Math.max(m, c.sortIndex ?? -1), -1)
        const nonDefaultCount = list.filter((c) => c.id !== UNCATEGORIZED_CATEGORY_ID).length
        const created: Category = {
          ...category,
          id,
          sortIndex: maxIdx + 1,
          color: category.color?.trim() || pickNextTableAreaColor(nonDefaultCount),
        }
        saveCategories([...list, created])
        return created
      })()
  appendAuditLog({
    domain: 'menu',
    action: 'category.created',
    message: `Created category ${full.name}`,
    entityType: 'category',
    entityId: full.id,
    meta: { name: full.name },
  })
  return full
}

function mergeCategoryPatchLocal(existing: Category, patch: Partial<Category>): Category {
  const next: Category = {
    ...existing,
    ...patch,
    color: patch.color?.trim() || existing.color,
  }
  if ('station' in patch) {
    const s = patch.station?.trim()
    next.station = s || undefined
  }
  return next
}

export function updateCategory(id: string, patch: Partial<Category>): Category[] {
  const before = getCategories().find((c) => c.id === id)
  const updated = useFirestore()
    ? firestoreCategories.updateCategory(id, patch)
    : (() => {
        const list = readCategoriesLocalUnsorted()
        const next = list.map((c) =>
          c.id === id ? mergeCategoryPatchLocal(c, patch) : c,
        )
        saveCategories(next)
        return getCategoriesLocal()
      })()
  const after = updated.find((c) => c.id === id)
  const keys = Object.keys(patch).join(', ')
  appendAuditLog({
    domain: 'menu',
    action: 'category.updated',
    message: `Updated category ${after?.name ?? before?.name ?? id} (${keys})`,
    entityType: 'category',
    entityId: id,
    meta: { name: after?.name ?? before?.name ?? '', keys },
  })
  return updated
}

export function replaceStationNameInCategories(
  oldName: string,
  newName: string,
): void {
  if (useFirestore()) {
    firestoreCategories.replaceStationNameInCategories(oldName, newName)
    return
  }
  const list = readCategoriesLocalUnsorted()
  const trimmed = newName.trim()
  if (!trimmed) return
  const updated = list.map((c) =>
    c.station === oldName ? { ...c, station: trimmed } : c,
  )
  if (updated.some((c, i) => c !== list[i])) saveCategories(updated)
}

export function clearStationFromCategories(stationName: string): void {
  if (useFirestore()) {
    firestoreCategories.clearStationFromCategories(stationName)
    return
  }
  const list = readCategoriesLocalUnsorted()
  const updated = list.map((c) => {
    if (c.station !== stationName) return c
    const { station: _s, ...rest } = c
    return rest
  })
  if (updated.some((c, i) => c !== list[i])) saveCategories(updated)
}

export function deleteCategory(id: string): Category[] {
  if (id === UNCATEGORIZED_CATEGORY_ID) return getCategories()
  const before = getCategories().find((c) => c.id === id)
  const updated = useFirestore()
    ? firestoreCategories.deleteCategory(id)
    : (() => {
        const filtered = readCategoriesLocalUnsorted().filter((c) => c.id !== id)
        saveCategories(filtered)
        return getCategoriesLocal()
      })()
  appendAuditLog({
    domain: 'menu',
    action: 'category.deleted',
    message: `Deleted category ${before?.name ?? id}`,
    entityType: 'category',
    entityId: id,
    meta: { name: before?.name ?? '' },
  })
  return updated
}

export function reorderCategories(orderedIds: string[]): void {
  if (useFirestore()) {
    firestoreCategories.reorderCategories(orderedIds)
    return
  }
  const categories = getCategoriesLocal()
  const byId = new Map(categories.map((c) => [c.id, c]))
  const newOrder = orderedIds
    .map((id) => byId.get(id))
    .filter((c): c is Category => c != null)
  saveCategories(newOrder)
}
